import { useMemo } from 'react'
import ReactECharts from 'echarts-for-react'
import { MetricTooltip } from './MetricTooltip'

export const CWV_THRESHOLDS = {
  LCP:  { good: 2500, poor: 4000 },
  FCP:  { good: 1800, poor: 3000 },
  CLS:  { good: 0.1,  poor: 0.25 },
  TTFB: { good: 800,  poor: 1800 },
  INP:  { good: 200,  poor: 500  },
} as const

type MetricKey = keyof typeof CWV_THRESHOLDS

interface CwvCardProps {
  metric: MetricKey
  value: number | null
  history?: number[]
  dates?: string[]
  projectId: string
}

const COLOR_GOOD   = '#10B981'
const COLOR_MEDIUM = '#F59E0B'
const COLOR_POOR   = '#EF4444'
const COLOR_NULL   = '#8B8FA7'

function getColor(metric: MetricKey, value: number | null): string {
  if (value === null) return COLOR_NULL
  const { good, poor } = CWV_THRESHOLDS[metric]
  if (value <= good) return COLOR_GOOD
  if (value <= poor) return COLOR_MEDIUM
  return COLOR_POOR
}

function formatValue(metric: MetricKey, value: number | null): string {
  if (value === null) return '\u2014'

  if (metric === 'CLS') {
    return value.toFixed(2)
  }

  if (value >= 1000) {
    const seconds = value / 1000
    return seconds % 1 === 0 ? `${seconds}s` : `${seconds.toFixed(1)}s`
  }
  return `${Math.round(value)}ms`
}

function formatDate(iso: string): string {
  const d = new Date(iso)
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })
}

function MetricChart({ data, dates, color, metric }: {
  data: number[]
  dates: string[]
  color: string
  metric: MetricKey
}) {
  const { good, poor } = CWV_THRESHOLDS[metric]
  const maxVal = Math.max(...data, poor * 1.2)

  const option = useMemo(() => ({
    backgroundColor: 'transparent',
    grid: { left: 0, right: 0, top: 8, bottom: 20 },
    xAxis: {
      type: 'category' as const,
      data: dates.map(formatDate),
      show: true,
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { show: true, fontSize: 9, color: '#8B8FA7', interval: Math.max(0, dates.length - 2) },
      boundaryGap: false,
    },
    yAxis: {
      type: 'value' as const,
      show: false,
      min: 0,
      max: metric === 'CLS' ? Math.max(...data, poor * 1.5) : maxVal,
    },
    series: [
      {
        type: 'line' as const,
        data,
        smooth: 0.3,
        symbol: 'circle',
        symbolSize: 0,
        emphasis: {
          scale: true,
          itemStyle: { borderColor: '#fff', borderWidth: 2 },
        },
        lineStyle: { color, width: 2 },
        areaStyle: {
          color: {
            type: 'linear',
            x: 0, y: 0, x2: 0, y2: 1,
            colorStops: [
              { offset: 0, color: color + '25' },
              { offset: 1, color: color + '05' },
            ],
          },
        },
        markLine: {
          silent: true,
          symbol: 'none',
          label: {
            fontSize: 9,
            position: 'insideEndTop' as const,
          },
          data: [
            {
              yAxis: good,
              lineStyle: { color: COLOR_GOOD, type: 'dashed' as const, width: 1 },
              label: { formatter: 'Bon', color: COLOR_GOOD },
            },
            {
              yAxis: poor,
              lineStyle: { color: COLOR_POOR, type: 'dashed' as const, width: 1 },
              label: { formatter: 'Mauvais', color: COLOR_POOR },
            },
          ],
        },
      },
    ],
    tooltip: {
      trigger: 'axis' as const,
      backgroundColor: '#1B2559',
      borderColor: '#1B2559',
      borderRadius: 8,
      padding: [6, 10],
      textStyle: { color: '#fff', fontSize: 11 },
      formatter: (params: any) => {
        const p = Array.isArray(params) ? params[0] : params
        if (!p) return ''
        const val = formatValue(metric, p.value)
        return `<span style="color:#8B8FA7;font-size:10px">${p.name}</span><br/>` +
               `<b style="color:${color}">${val}</b>`
      },
    },
  }), [data, dates, color, metric, good, poor, maxVal])

  return (
    <ReactECharts
      option={option}
      style={{ height: 120, width: '100%' }}
      opts={{ renderer: 'svg' }}
    />
  )
}

export function CwvCard({ metric, value, history, dates, projectId: _projectId }: CwvCardProps) {
  const color        = getColor(metric, value)
  const displayValue = formatValue(metric, value)
  const showChart    = history !== undefined && history.length >= 2 && dates !== undefined

  const bgClass = value === null
    ? 'bg-white border-gray-200'
    : value <= CWV_THRESHOLDS[metric].good
    ? 'bg-emerald-50/50 border-emerald-200'
    : value <= CWV_THRESHOLDS[metric].poor
    ? 'bg-amber-50/50 border-amber-200'
    : 'bg-red-50/50 border-red-200'

  return (
    <div className={`border rounded-lg p-3 cursor-pointer hover:shadow-sm transition-shadow w-[200px] ${bgClass}`}>
      {/* Header */}
      <div className="flex items-center gap-1 mb-1">
        <span className="text-xs font-semibold text-[#8B8FA7] uppercase tracking-wide">
          {metric}
        </span>
        <MetricTooltip metric={metric} />
      </div>

      {/* Valeur */}
      <div
        className="text-2xl font-bold leading-none mb-1"
        style={{ color }}
      >
        {displayValue}
      </div>

      {/* Chart interactif */}
      {showChart && (
        <div className="-mx-1 mt-2">
          <MetricChart data={history!} dates={dates!} color={color} metric={metric} />
        </div>
      )}
    </div>
  )
}

export default CwvCard
